import React from "react";

// ===================================================================
// The Crowd v2 — Courbe d'evolution
//
// Composant partage. SVG a largeur fluide : le viewBox est fixe, le
// trace s'adapte a la largeur du conteneur. Couleurs dans
// v2-composants.css (classes v2-courbe-*), jamais en dur.
//
// Props :
//   data        [{ ... }] — un point par periode, dans l'ordre
//   xKey        cle du libelle d'abscisse (ex. "mois" : "2026-03")
//   yKey        cle de la valeur
//   formatX     fn(libelle) -> texte affiche sous l'axe — optionnel
//   unite       texte ajoute a la valeur dans l'infobulle — optionnel
// ===================================================================

const LARGEUR = 1000;
const HAUTEUR = 240;
const MARGE = { haut: 20, droite: 20, bas: 36, gauche: 44 };

function GraphiqueCourbe({ data, xKey, yKey, formatX = (x) => x, unite = "" }) {
  if (!data || data.length < 2) {
    return <p className="v2-vide">Pas assez de données</p>;
  }

  const maxVal = Math.max(...data.map((d) => d[yKey]));
  const largeurUtile = LARGEUR - MARGE.gauche - MARGE.droite;
  const hauteurUtile = HAUTEUR - MARGE.haut - MARGE.bas;

  const points = data.map((d, i) => ({
    x: MARGE.gauche + (i / (data.length - 1)) * largeurUtile,
    y: MARGE.haut + hauteurUtile - (maxVal > 0 ? (d[yKey] / maxVal) * hauteurUtile : 0),
    libelle: d[xKey],
    valeur: d[yKey],
  }));

  const trace = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

  // Un libelle d'abscisse sur deux au-dela de 12 points, pour rester lisible
  const pas = data.length > 12 ? 2 : 1;

  return (
    <svg
      className="v2-courbe"
      viewBox={`0 0 ${LARGEUR} ${HAUTEUR}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Évolution"
    >
      {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
        const y = MARGE.haut + hauteurUtile - ratio * hauteurUtile;
        return (
          <g key={ratio}>
            <line className="v2-courbe-grille" x1={MARGE.gauche} y1={y} x2={LARGEUR - MARGE.droite} y2={y} />
            <text className="v2-courbe-texte" x={MARGE.gauche - 10} y={y + 4} textAnchor="end">
              {Math.round(maxVal * ratio)}
            </text>
          </g>
        );
      })}

      <path className="v2-courbe-trace" d={trace} />

      {points.map((p, i) => (
        <g key={i}>
          <circle className="v2-courbe-point" cx={p.x} cy={p.y} r="5">
            <title>{`${p.libelle} : ${p.valeur}${unite}`}</title>
          </circle>
          {i % pas === 0 && (
            <text className="v2-courbe-texte" x={p.x} y={HAUTEUR - 10} textAnchor="middle">
              {formatX(p.libelle)}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

export default GraphiqueCourbe;
